import { ClockIcon, PencilIcon, ViewListIcon } from "@heroicons/react/outline";
import {
  ClockIcon as ClockIconSolid,
  PencilIcon as PencilIconSolid,
  ViewListIcon as ViewListIconSolid,
} from "@heroicons/react/solid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";

import Changelog from "../Changelog";
import BreadcrumbsPageWrapper from "../UnifiedRegistry/BreadcrumbsPageWrapper";
import { useDeleteEntity } from "../UnifiedRegistry/Dialogs/hooks/useDeleteEntity";
import useFolderPathBreadcrumbs from "../UnifiedRegistry/hooks/useFolderPathBreadcrumbs";

import DeploymentGuideDialog from "./DeploymentGuideDialog";
import EditorContent from "./EditorContent";
import { usePromptTemplateData, useTabNavigation } from "./hooks";
import OverviewContent from "./OverviewContent";
import PromptTemplatePresetEvaluationsDialog from "./PresetEvaluationsDialog";
import PromptTemplateActionsMenu from "./PromptTemplateActionsMenu";

import { ResourceHeader } from "@/components/ui/ResourceHeader";
import { TabItem } from "@/components/ui/TabNavigation/types";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useNavigateWithSearch } from "@/hooks";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType, useEditPromptTemplate, useGetChangelogPendingCount } from "@/queries";
import { captureSmartTableEvent } from "@/queries/smart-tables/analytics";
import { getTemplateFormat } from "@/utils/utils";

const PromptTemplate = () => {
  const { promptId, versionId, sessionId } = useParams();
  const { promptVersion, promptRegistry, totalVersions, promptName } = usePromptTemplateData(
    promptId!,
    versionId,
  );

  const isSnippet = promptVersion.data?.is_snippet ?? promptRegistry.data?.is_snippet ?? false;

  const [overviewSessionId, setOverviewSessionId] = useState(() => sessionId || v4());
  const navigate = useNavigate();
  const navigateWithSearch = useNavigateWithSearch();
  const [isDeploymentGuideModalOpen, setDeploymentGuideModalOpen] = useState(false);
  const [isPresetEvaluationsDialogOpen, setPresetEvaluationsDialogOpen] = useState(false);

  useEffect(() => {
    setOverviewSessionId(sessionId || v4());
  }, [sessionId, versionId]);

  const { showVersions, setShowVersions } = useTabNavigation();
  const { deleteEntity } = useDeleteEntity();

  const editPromptTemplate = useEditPromptTemplate();
  const { activeWorkspaceId } = useUser();
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const { isEditDisabled, isDeleteDisabled } = useRbac();

  const cannotEditEvaluation = isEditDisabled(EntityType.REPORT);

  const handleOpenPresetEvaluationsDialog = useCallback(() => {
    captureSmartTableEvent("Smart Table Prompt Entry Clicked", {
      workspace_id: activeWorkspaceId ?? null,
      source: "prompt-template-header",
      prompt_registry_id: promptId ? Number(promptId) : null,
      prompt_version_number: versionId ? Number(versionId) : null,
    });
    setPresetEvaluationsDialogOpen(true);
  }, [activeWorkspaceId, promptId, versionId]);

  const { data: pendingChangelogCount } = useGetChangelogPendingCount({
    entityId: Number(promptId),
    entityType: EntityType.PROMPT,
    enabled: !!promptId,
  });

  const tabs = useMemo<TabItem[]>(() => {
    const editorTab: TabItem = {
      id: 1,
      name: "Editor",
      outlineIcon: <PencilIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />,
      solidIcon: <PencilIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
      content: (
        <EditorContent
          isLoading={promptVersion.isLoading}
          instanceId={overviewSessionId}
          promptVersion={promptVersion.data}
          isSnippet={isSnippet}
        />
      ),
      hasId: true,
      path: `/workspace/${activeWorkspaceId}/prompt/${promptId}/version/${versionId}/playground`,
      className: "relative",
    };

    const baseTabs: TabItem[] = [
      {
        id: 0,
        name: "Overview",
        outlineIcon: <ViewListIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        solidIcon: <ViewListIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
        content: (
          <OverviewContent
            promptVersion={promptVersion}
            showVersions={showVersions}
            setShowVersions={setShowVersions}
            totalVersions={totalVersions}
            navigateToPlayground={() =>
              navigateWithSearch(
                `/workspace/${activeWorkspaceId}/prompt/${promptId}/version/${versionId}/playground/${overviewSessionId}`,
              )
            }
            onEvaluate={handleOpenPresetEvaluationsDialog}
            isEvaluateDisabled={cannotEditEvaluation}
          />
        ),
        path: `/workspace/${activeWorkspaceId}/prompt/${promptId}/version/${versionId}`,
      },
      editorTab,
    ];

    baseTabs.push({
      id: 2,
      name: "Changelog",
      outlineIcon: <ClockIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />,
      solidIcon: <ClockIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
      count: pendingChangelogCount,
      content: (
        <Changelog
          entity={{
            id: Number(promptId),
            type: EntityType.PROMPT,
          }}
        />
      ),
      path: `/workspace/${activeWorkspaceId}/prompt/${promptId}/version/${versionId}/changelog`,
    });

    return baseTabs;
  }, [
    isSnippet,
    promptVersion,
    showVersions,
    setShowVersions,
    totalVersions,
    navigateWithSearch,
    activeWorkspaceId,
    promptId,
    versionId,
    overviewSessionId,
    pendingChangelogCount,
    handleOpenPresetEvaluationsDialog,
    cannotEditEvaluation,
  ]);

  const handleTitleEdit = useCallback(
    ({
      newTitle,
      onSuccess,
      onError,
    }: {
      newTitle: string;
      onSuccess?: (newTitle?: string) => void;
      onError?: () => void;
    }) => {
      if (!promptRegistry.data) {
        onError?.();
        return;
      }
      editPromptTemplate.mutate(
        {
          prompt_id: Number(promptId),
          userToken,
          basePromptTemplate: {
            prompt_name: newTitle,
            tags: promptRegistry.data.tags,
            folder_id: promptRegistry.data.folder_id!,
            workspace_id: activeWorkspaceId!,
          },
          activeWorkspaceId: activeWorkspaceId!,
        },
        {
          onSuccess: () => {
            onSuccess?.(newTitle);
          },
          onError: () => {
            onError?.();
          },
        },
      );
    },
    [promptRegistry.data, promptId, userToken, activeWorkspaceId, editPromptTemplate],
  );

  const handleTagsEdit = useCallback(
    (tags: string[]) => {
      if (!promptRegistry.data) {
        return;
      }
      editPromptTemplate.mutate({
        prompt_id: Number(promptId),
        userToken,
        basePromptTemplate: {
          prompt_name: promptRegistry.data.prompt_name,
          tags,
          folder_id: promptRegistry.data.folder_id!,
          workspace_id: activeWorkspaceId!,
        },
        activeWorkspaceId: activeWorkspaceId!,
      });
    },
    [promptRegistry.data, promptId, userToken, activeWorkspaceId, editPromptTemplate],
  );

  const handleDeploymentGuideClick = useCallback(() => {
    setDeploymentGuideModalOpen(true);
  }, []);

  const handleDeletePrompt = useCallback(() => {
    if (!promptRegistry.data) {
      return;
    }

    deleteEntity(
      {
        id: promptRegistry.data.id,
        name: promptRegistry.data.prompt_name,
        type: EntityType.PROMPT,
        path: [],
      },
      () => {
        navigate(`/workspace/${activeWorkspaceId}/home`);
      },
    );
  }, [promptRegistry.data, deleteEntity, navigate, activeWorkspaceId]);

  // Get input variables from the prompt template
  const inputVariables =
    promptVersion.data?.prompt_template.input_variables ||
    promptVersion.data?.rendered_template?.input_variables ||
    [];

  // Get the most recent label if available
  const mostRecentLabel =
    promptVersion.data?.labels && promptVersion.data.labels.length > 0
      ? promptVersion.data.labels[0]?.name
      : null;

  const breadcrumbProps = useFolderPathBreadcrumbs(promptRegistry.data?.folder_id ?? 0, promptName);

  return (
    <div className="flex h-full flex-col">
      <BreadcrumbsPageWrapper
        {...breadcrumbProps}
        className="flex h-full flex-col pb-3"
        entityTitle={{
          type: isSnippet ? EntityType.SNIPPET : EntityType.PROMPT,
          title: promptName || "",
          onTitleEdit: handleTitleEdit,
          isLoading: promptRegistry.isLoading,
        }}
      >
        <ResourceHeader
          type={isSnippet ? EntityType.SNIPPET : EntityType.PROMPT}
          titleInBreadcrumb
          showVersions={showVersions}
          onToggleVersions={() => setShowVersions(!showVersions)}
          isLoading={promptRegistry.isLoading}
          title={promptName || ""}
          minimal
          onTitleEdit={handleTitleEdit}
          renderRightContent={
            activeWorkspaceId && promptId ? (
              <div className="flex items-center gap-1">
                <PromptTemplateActionsMenu
                  canShowDeploymentGuide={!isSnippet}
                  canShowRelatedContent={!isSnippet}
                  deleteDisabled={isDeleteDisabled(EntityType.PROMPT)}
                  deleteTooltip={
                    isDeleteDisabled(EntityType.PROMPT)
                      ? "You don't have permission to delete this prompt"
                      : undefined
                  }
                  onDelete={handleDeletePrompt}
                  onOpenDeploymentGuide={handleDeploymentGuideClick}
                />
              </div>
            ) : undefined
          }
          id={promptId!}
          format={
            (promptVersion.data && getTemplateFormat(promptVersion.data.prompt_template)) ||
            "f-string"
          }
          tags={promptRegistry.data?.tags || []}
          onTagsEdit={handleTagsEdit}
          createdAt={promptRegistry.data?.created_at || ""}
          actions={[]}
          tabsProps={{
            tabs: tabs,
          }}
        />
      </BreadcrumbsPageWrapper>

      <DeploymentGuideDialog
        open={isDeploymentGuideModalOpen}
        onOpenChange={setDeploymentGuideModalOpen}
        promptName={promptName}
        inputVariables={inputVariables}
        mostRecentLabel={mostRecentLabel}
      />

      {promptId && versionId && (
        <PromptTemplatePresetEvaluationsDialog
          open={isPresetEvaluationsDialogOpen}
          onOpenChange={setPresetEvaluationsDialogOpen}
          promptRegistryId={Number(promptId)}
          promptVersionNumber={Number(versionId)}
          promptVersion={promptVersion.data}
          promptName={promptName}
          folderId={promptRegistry.data?.folder_id ?? promptVersion.data?.folder_id ?? null}
        />
      )}
    </div>
  );
};

export default PromptTemplate;
